export type TarBuildOptions = {
  onProgress?: (pct: number) => void;
};

const enc = new TextEncoder();

const pad512 = (n: number) => Math.ceil(n / 512) * 512;

const writeField = (buf: Uint8Array, str: string, offset: number, len: number) => {
  const bytes = enc.encode(str);
  buf.set(bytes.slice(0, len), offset);
};

const writeOctal = (buf: Uint8Array, n: number, offset: number, len: number) => {
  const s = n.toString(8).padStart(len - 1, '0') + '\0';
  writeField(buf, s, offset, len);
};

const computeChecksum = (header: Uint8Array) => {
  let sum = 0;
  for (let i = 0; i < 512; i++) sum += header[i];
  return sum;
};

const buildUstarHeader = (args: {
  name: string;
  size: number;
  mtime: number;
  typeflag?: string;
}) => {
  const header = new Uint8Array(512);
  const name = args.name;
  const size = args.size;
  const mtime = args.mtime;
  const typeflag = (args.typeflag || '0').slice(0, 1);

  // Long name support (ustar): split into prefix (<=155) + name (<=100) at a '/' boundary.
  // If still not possible, we will rely on PAX extended headers; so we keep a safe placeholder name.
  let prefix = '';
  let filename = name;

  if (name.length > 100) {
    const cutAt = name.lastIndexOf('/', 155);
    if (cutAt > 0 && name.length - cutAt - 1 <= 100) {
      prefix = name.slice(0, cutAt);
      filename = name.slice(cutAt + 1);
    } else {
      filename = name.slice(0, 100);
    }
  }

  writeField(header, filename, 0, 100);
  writeField(header, '0000644\0', 100, 8);
  writeField(header, '0000000\0', 108, 8);
  writeField(header, '0000000\0', 116, 8);
  writeOctal(header, size, 124, 12);
  writeOctal(header, mtime, 136, 12);
  header.fill(32, 148, 156);
  header[156] = enc.encode(typeflag)[0];
  writeField(header, 'ustar\0', 257, 6);
  writeField(header, '00', 263, 2);
  if (prefix) writeField(header, prefix, 345, 155);

  const sum = computeChecksum(header);
  writeField(header, sum.toString(8).padStart(6, '0') + '\0 ', 148, 8);
  return header;
};

const buildPaxRecords = (records: Record<string, string>) => {
  const parts: Uint8Array[] = [];
  Object.entries(records).forEach(([k, v]) => {
    const kv = `${k}=${v}\n`;
    // Length includes the length digits + space + kv
    // We compute fixed-point because the digit count depends on the length itself.
    let len = kv.length + 3; // minimal guess: "0 " + kv
    // iterate until stable
    for (let i = 0; i < 5; i++) {
      const digits = String(len).length;
      const next = digits + 1 + kv.length;
      if (next === len) break;
      len = next;
    }
    const line = `${len} ${kv}`;
    parts.push(enc.encode(line));
  });
  const total = parts.reduce((acc, p) => acc + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  parts.forEach((p) => {
    out.set(p, off);
    off += p.length;
  });
  return out;
};

const needsPaxPath = (name: string) => {
  if (name.length > 255) return true;
  if (name.length <= 100) return false;
  const cutAt = name.lastIndexOf('/', 155);
  if (cutAt > 0 && name.length - cutAt - 1 <= 100) return false;
  return true;
};

export const buildTarBlob = async (files: File[], opts?: TarBuildOptions): Promise<Blob> => {
  const chunks: Uint8Array[] = [];
  const mtime = Math.floor(Date.now() / 1000);

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const name = ((file as any).webkitRelativePath as string) || file.name;
    const data = new Uint8Array(await file.arrayBuffer());

    // PAX extended header if needed (path too long for ustar)
    if (needsPaxPath(name)) {
      const paxData = buildPaxRecords({ path: name });
      chunks.push(buildUstarHeader({ name: 'PaxHeader', size: paxData.length, mtime, typeflag: 'x' }));
      const paxPadded = new Uint8Array(pad512(paxData.length));
      paxPadded.set(paxData, 0);
      chunks.push(paxPadded);
    }

    chunks.push(buildUstarHeader({ name, size: data.length, mtime, typeflag: '0' }));

    const padded = new Uint8Array(pad512(data.length));
    padded.set(data, 0);
    chunks.push(padded);

    opts?.onProgress?.(Math.round(((i + 1) / files.length) * 90));
  }

  chunks.push(new Uint8Array(1024));
  return new Blob(chunks as BlobPart[], { type: 'application/x-tar' });
};
